<template>
  <teleport :to="teleportTarget">
    <WdsModal :show :title data-test="employment-onboard-modal" size="lg" @cancel="handleCancel">
      <div class="tw-space-y-6">
        <WdsAlert info-subtle icon bordered>
          {{ $t('Only fill out applicable cost fields and confirm the values') }}
        </WdsAlert>

        <div class="tw-space-y-2">
          <div class="tw-space-y-1">
            <WdsText>{{ $t('Ongoing cost') }}</WdsText>
            <WdsText secondary>
              {{ $t('Added to each invoice for the duration of employment') }}
            </WdsText>
          </div>

          <div class="tw-flex tw-items-end tw-justify-between tw-gap-4">
            <div class="tw-w-40">
              <WdsText small secondary class="tw-mb-1">{{ $t('Fixed amount') }}</WdsText>
              <WdsInputCurrency
                id="fixed-amount"
                v-model="formEmploymentCosts.fixedAmount"
                :disabled="loading || employmentCostsLoading"
                :config="currencyConfig"
                placeholder="0"
                data-test="fixed-amount-input"
              />
            </div>

            <div class="tw-h-9 tw-flex tw-items-center tw-justify-center tw-px-2">
              <WdsText small secondary>{{ $t('and / or') }}</WdsText>
            </div>

            <div class="tw-w-40">
              <WdsText small secondary class="tw-mb-1">{{ $t('Percentage') }}</WdsText>
              <div class="tw-relative">
                <WdsInput
                  id="percentage"
                  v-model="formEmploymentCosts.percentage"
                  :disabled="loading || employmentCostsLoading"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="0"
                  data-test="percentage-input"
                />
                <span
                  class="tw-absolute tw-right-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-sm tw-text-gray-500"
                >
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        <WdsDivider />

        <div class="tw-flex tw-items-start tw-gap-4">
          <div class="tw-flex tw-items-start tw-gap-3 tw-flex-1">
            <WdsToggle
              id="one-time-payment"
              v-model:checked="formEmploymentCosts.enableOneTimePayment"
              data-test="one-time-payment-toggle"
              class="tw-mt-1"
            />
            <div class="tw-space-y-1">
              <WdsText>{{ $t('Add one-time payment') }}</WdsText>
              <WdsText secondary>{{ $t('Applies only to the first invoice') }}</WdsText>
            </div>
          </div>

          <div class="tw-w-50 tw-flex-shrink-0">
            <WdsInputCurrency
              id="one-time-amount"
              v-model="formEmploymentCosts.oneTimeAmount"
              :disabled="
                loading || employmentCostsLoading || !formEmploymentCosts.enableOneTimePayment
              "
              :config="currencyConfig"
              placeholder="0"
              data-test="one-time-amount-input"
              :class="{ 'tw-opacity-50': !formEmploymentCosts.enableOneTimePayment }"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <WdsStack justify-space-between align-center>
          <WdsButton
            tertiary
            class="tw-bg-gray-100 tw-text-gray-700 tw-border-gray-200 hover:tw-bg-gray-200"
            data-test="onboarding-cancel-button"
            @click="handleCancel"
          >
            {{ $t('Cancel onboarding') }}
          </WdsButton>

          <WdsStack align-center gap-md>
            <WdsSpinner v-if="loading" indeterminate />

            <WdsButton
              primary
              :disabled="loading"
              data-test="onboarding-next-button"
              class="tw-bg-gray-900 tw-text-white hover:tw-bg-gray-800 tw-border-gray-900"
              @click="handleNextOrSubmit"
            >
              {{ $t('Confirm') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script lang="ts" setup>
import { computed, watch, reactive } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsButton,
  WdsDivider,
  WdsInput,
  WdsInputCurrency,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsToggle,
} from '@worksome/wds-components'
import { forEach } from 'lodash'
import swal from 'sweetalert'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, graphql, useFragment } from '@/gql'
import { EmploymentCostType, EmploymentCostFrequency } from '@/gql/graphql'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const EmploymentOnboardModal = graphql(`
  fragment EmploymentOnboardModal on Employment {
    id
    hires {
      data {
        id
        currency
        createdAt
      }
    }
    worker {
      id
      name
    }
  }
`)

const props = defineProps<{
  employment?: FragmentType<typeof EmploymentOnboardModal> | null
}>()

const emit = defineEmits<{
  onboarded: []
}>()

const show = defineModel<boolean>('show')

const employment = computed(() =>
  props.employment ? useFragment(EmploymentOnboardModal, props.employment) : null,
)

// GraphQL queries and mutations
const GET_EMPLOYMENT_COSTS = graphql(`
  query GetEmploymentCosts($id: ID!) {
    employment(id: $id) {
      id
      employmentCosts {
        data {
          id
          type
          frequency
          amount
        }
      }
    }
  }
`)

const ONBOARD_EMPLOYMENT = graphql(`
  mutation OnboardEmployment($input: OnboardEmploymentInput!) {
    onboardEmployment(input: $input) {
      id
      isOnboarded
      firstPayrolledAt
    }
  }
`)

const defaultCurrencyCode = computed(() => employment.value?.hires?.data?.at(-1)?.currency || '')

const formEmploymentCosts = reactive({
  fixedAmount: '',
  percentage: '',
  enableOneTimePayment: false,
  oneTimeAmount: '',
})

const resetForm = () => {
  formEmploymentCosts.fixedAmount = ''
  formEmploymentCosts.percentage = ''
  formEmploymentCosts.oneTimeAmount = ''
  formEmploymentCosts.enableOneTimePayment = false
}

watch(
  () => show.value,
  () => resetForm(),
)

const shouldFetchEmploymentCosts = computed(() => !!show.value && !!employment.value?.id)

const { onResult: prefillEmploymentCosts, loading: employmentCostsLoading } = useQuery(
  GET_EMPLOYMENT_COSTS,
  () => ({ id: employment.value?.id ?? '' }),
  {
    enabled: shouldFetchEmploymentCosts,
    fetchPolicy: 'no-cache',
    clientId: 'publicApi',
  },
)

prefillEmploymentCosts((results) => {
  if (results.data?.employment?.employmentCosts?.data) {
    const employmentCosts = results.data.employment.employmentCosts.data

    for (const cost of employmentCosts) {
      if (
        cost.type === EmploymentCostType.Fixed &&
        cost.frequency === EmploymentCostFrequency.Ongoing
      ) {
        formEmploymentCosts.fixedAmount = cost.amount?.toString() || ''
      } else if (
        cost.type === EmploymentCostType.Percentage &&
        cost.frequency === EmploymentCostFrequency.Ongoing
      ) {
        formEmploymentCosts.percentage = cost.amount?.toString() || ''
      } else if (
        cost.type === EmploymentCostType.Fixed &&
        cost.frequency === EmploymentCostFrequency.OneTime
      ) {
        formEmploymentCosts.oneTimeAmount = cost.amount?.toString() || ''
        formEmploymentCosts.enableOneTimePayment = cost.amount !== null && cost.amount > 0
      }
    }
  }
})

const {
  mutate: onboardEmployment,
  loading: onboardingLoading,
  error,
} = useMutation(ONBOARD_EMPLOYMENT, {
  clientId: 'publicApi',
})

const loading = computed(() => employmentCostsLoading.value || onboardingLoading.value)

const teleportTarget = useTableGlobalsSelector('body')

const title = computed(() => {
  const workerName = employment.value?.worker?.name || 'Worker'
  return $t('Confirm employment costs for :workerName', { workerName })
})

const currencyConfig = computed(() => ({
  currencySymbol: defaultCurrencyCode.value || '',
  thousandsSeparator: ',',
  decimalSeparator: '.',
  showFractions: true,
}))

const payload = computed(() => {
  const employmentCosts: {
    fixedAmount?: number
    percentage?: number
    oneTimeAmount?: number
  } = {}

  forEach(['fixedAmount', 'percentage', 'oneTimeAmount'], (key) => {
    if (formEmploymentCosts[key]) {
      employmentCosts[key] = parseFloat(formEmploymentCosts[key])
    }
  })

  return {
    input: {
      employment: employment.value!.id,
      employmentCosts,
    },
  }
})

const handleCancel = () => {
  show.value = false
}
const handleNextOrSubmit = async () => {
  await onboardEmployment(payload.value)

  if (error.value) {
    swal({
      title: $t('Error'),
      text: $t('Could not onboard employment'),
      type: 'error',
      showConfirmButton: false,
      allowOutsideClick: true,
    })
  } else {
    emit('onboarded')
    show.value = false
  }
}

watch(
  () => formEmploymentCosts.enableOneTimePayment,
  (enabled) => {
    if (!enabled) {
      // Clear the one-time amount when checkbox is unchecked
      formEmploymentCosts.oneTimeAmount = ''
    }
  },
)
</script>

<style scoped lang="scss">
:deep(.wds-modal) {
  z-index: 5000 !important;
}

:deep(.wds-modal-backdrop) {
  z-index: 4999 !important;
}
</style>
